import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, NavLink, Outlet } from "react-router";

import { api, OriginRefused, RateLimited, Unauthorized } from "./api";

/**
 * The shell: authentication, and everything else behind it.
 *
 * Login is rendered in place of the app rather than redirected to, so a 401
 * arriving mid-poll does not lose the URL the person was looking at.
 */
export function App() {
  const me = useQuery({ queryKey: ["me"], queryFn: api.me, retry: false });

  if (me.isPending) return <p className="muted">Loading…</p>;
  // **Only a 401 means "sign in".** `!me.data` is also true for a 500, a
  // restarted API, an unreachable database and a refused origin — and throwing
  // the operator to a bare login form for any of those tells them their session
  // ended when it did not. They then type correct credentials into a form that
  // cannot possibly work, which is the same confidently-wrong-diagnosis failure
  // `LoginError` below exists to end, one branch higher up.
  if (me.error && !(me.error instanceof Unauthorized)) {
    return (
      <div className="login">
        <h1>Auto Heal</h1>
        <LoginError error={me.error} />
        <p className="muted">
          Your session was not rejected — the control plane could not be reached.
          Retrying will not need your password.
        </p>
        <button type="button" onClick={() => me.refetch()} disabled={me.isFetching}>
          {me.isFetching ? "Retrying…" : "Retry"}
        </button>
      </div>
    );
  }
  if (!me.data) return <Login />;

  return (
    <div className="shell">
      <header>
        <Link to="/" className="brand">
          Auto&nbsp;Heal
        </Link>
        <nav>
          <NavLink to="/" end>
            Runs
          </NavLink>
          <NavLink to="/projects">Projects</NavLink>
          <NavLink to="/operations">Operations</NavLink>
        </nav>
        <span className="muted">{me.data.email}</span>
        <LogoutButton />
      </header>
      <main>
        <Outlet />
      </main>
    </div>
  );
}

function LogoutButton() {
  const queries = useQueryClient();
  const logout = useMutation({
    mutationFn: api.logout,
    onSuccess: () => queries.clear(),
  });
  return (
    <>
      <button type="button" onClick={() => logout.mutate()} disabled={logout.isPending}>
        Sign out
      </button>
      {/* A failed sign-out looks exactly like a slow one: the button re-enables
          and the email stays in the header. On a shared machine that is someone
          walking away from a live session that can push to production. */}
      {logout.error ? (
        <span className="error">Still signed in — {logout.error.message}</span>
      ) : null}
    </>
  );
}

function Login() {
  const queries = useQueryClient();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const login = useMutation({
    mutationFn: () => api.login(email, password),
    onSuccess: () => queries.invalidateQueries({ queryKey: ["me"] }),
  });

  return (
    <form
      className="login"
      onSubmit={(event) => {
        event.preventDefault();
        login.mutate();
      }}
    >
      <h1>Auto Heal</h1>
      <label>
        Email
        <input
          type="email"
          value={email}
          autoComplete="username"
          onChange={(event) => setEmail(event.target.value)}
          required
        />
      </label>
      <label>
        Password
        <input
          type="password"
          value={password}
          autoComplete="current-password"
          onChange={(event) => setPassword(event.target.value)}
          required
        />
      </label>
      <button type="submit" disabled={login.isPending}>
        {login.isPending ? "Signing in…" : "Sign in"}
      </button>
      <LoginError error={login.error} />
    </form>
  );
}


/**
 * Why the login failed — and **only "invalid email or password" when it was**.
 *
 * This showed that one sentence for every error, so a deployment whose
 * `AUTOHEAL_ALLOWED_ORIGINS` did not list the address the browser was actually
 * on reported a wrong password. The credentials were fine; the person checking
 * them had been sent to the wrong place by the only message they could see.
 *
 * The rule the rest of this codebase follows applies here too: a message that is
 * confidently wrong costs more than one that admits what it does not know.
 */
function LoginError({ error }: { error: Error | null }) {
  if (!error) return null;

  if (error instanceof RateLimited) {
    // Shown with its countdown: the single admin can otherwise lock themselves
    // out with no explanation.
    return (
      <p className="error">
        Too many attempts. Try again in about{" "}
        {Math.ceil(error.retryAfterSeconds / 60)} minutes.
      </p>
    );
  }

  if (error instanceof OriginRefused) {
    return (
      <p className="error">
        This address is not one the server accepts sign-ins from. Nothing is
        wrong with your password.
        <br />
        <span className="muted">
          Add <code>{window.location.origin}</code> to{" "}
          <code>AUTOHEAL_ALLOWED_ORIGINS</code>, or use the configured hostname.
        </span>
      </p>
    );
  }

  if (error instanceof Unauthorized) {
    return <p className="error">Invalid email or password.</p>;
  }

  // Anything else is shown as itself. Guessing at a cause here is how the
  // original bug happened.
  return <p className="error">Could not sign in: {error.message}</p>;
}
